import React, { useEffect, useState } from 'react';
import { 
    Layout, 
    Card, 
    Table, 
    Tag, 
    Button, 
    Input, 
    Row, 
    Col, 
    Space, 
    message, 
    Popconfirm, 
    Modal, 
    Form, 
    Breadcrumb, 
    Spin,
    Select
} from 'antd';
import { 
    PlusOutlined, 
    EditOutlined, 
    DeleteOutlined, 
    SearchOutlined, 
    ClearOutlined,
    DatabaseOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import Headerdiv from '../layout/header';
import Footerdiv from '../layout/footer';
import axiosClient from '../../api/axiosClient';
import { APPBACKGROUND_COLOR } from '../appsettings';

const { Content } = Layout;
const { Option } = Select;

const TypeManagement = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [filters, setFilters] = useState({ search: '', status: 'all' });
    
    const [modalVisible, setModalVisible] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [form] = Form.useForm();
    const [saveLoading, setSaveLoading] = useState(false);

    const fetchTypes = async (page = pagination.current, pageSize = pagination.pageSize) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page,
                pageSize,
                ...filters
            });
            const response = await axiosClient.get(`/master/type/get_all.php?${params.toString()}`);
            if (response.data.status === 'success') {
                setData(response.data.data);
                setPagination({
                    ...pagination,
                    current: page,
                    total: response.data.total
                });
            }
        } catch (error) {
            message.error('Failed to fetch types');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTypes(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleTableChange = (newPagination) => {
        fetchTypes(newPagination.current, newPagination.pageSize);
    };

    const handleSearch = () => {
        fetchTypes(1);
    };

    const handleClear = () => {
        setFilters({ search: '', status: 'all' });
        setLoading(true);
        axiosClient.get(`/master/type/get_all.php?page=1&pageSize=${pagination.pageSize}&search=&status=all`)
            .then(res => {
                if (res.data.status === 'success') {
                    setData(res.data.data);
                    setPagination({ ...pagination, current: 1, total: res.data.total });
                }
            })
            .finally(() => setLoading(false));
    };

    const showModal = (item = null) => {
        setEditingItem(item);
        if (item) {
            form.setFieldsValue({ ...item, status: Number(item.status) });
        } else {
            form.resetFields();
            form.setFieldsValue({ status: 1 });
        }
        setModalVisible(true);
    };

    const handleSave = async (values) => {
        setSaveLoading(true);
        try {
            const payload = {
                ...values,
                id: editingItem ? editingItem.id : 0
            };
            const response = await axiosClient.post('/master/type/upsert.php', payload);
            if (response.data.status === 'success') {
                message.success(response.data.message);
                setModalVisible(false);
                fetchTypes();
            } else {
                message.error(response.data.message);
            }
        } catch (error) {
            message.error('Failed to save type');
        } finally {
            setSaveLoading(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            const response = await axiosClient.post('/master/type/delete.php', { id });
            if (response.data.status === 'success') {
                message.success(response.data.message);
                fetchTypes();
            } else {
                message.error(response.data.message);
            }
        } catch (error) {
            message.error('Failed to delete type');
        }
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            width: 80,
        },
        {
            title: 'Type Name',
            dataIndex: 'value',
            key: 'value',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            width: 120,
            render: (status) => (
                <Tag color={Number(status) === 1 ? 'green' : 'red'}>
                    {Number(status) === 1 ? 'ACTIVE' : 'INACTIVE'}
                </Tag>
            )
        },
        {
            title: 'Action',
            key: 'action',
            width: 150,
            render: (_, record) => (
                <Space>
                    <Button 
                        type="primary" 
                        icon={<EditOutlined />} 
                        size="small"
                        onClick={() => showModal(record)}
                        style={{ backgroundColor: '#ffc107', borderColor: '#ffc107' }}
                    />
                    <Popconfirm
                        title="Delete this type?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button type="primary" danger icon={<DeleteOutlined />} size="small" />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <Layout style={{ backgroundColor: APPBACKGROUND_COLOR, minHeight: '100vh' }}>
            <Headerdiv />
            <Content style={{ padding: '24px' }}>
                <Breadcrumb style={{ marginBottom: 16 }}>
                    <Breadcrumb.Item><Link to="/dash">Dashboard</Link></Breadcrumb.Item>
                    <Breadcrumb.Item>Master Data</Breadcrumb.Item>
                    <Breadcrumb.Item>Inventory Types</Breadcrumb.Item>
                </Breadcrumb>

                <Card 
                    title={
                        <Space>
                            <DatabaseOutlined />
                            <span>Inventory Type Management</span>
                        </Space>
                    }
                    extra={
                        <Button type="primary" icon={<PlusOutlined />} onClick={() => showModal()}>
                            Add Type
                        </Button>
                    }
                >
                    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                        <Col xs={24} sm={12} md={8}>
                            <Input 
                                placeholder="Search by name..." 
                                prefix={<SearchOutlined />} 
                                value={filters.search}
                                onChange={e => setFilters({...filters, search: e.target.value})}
                                onPressEnter={handleSearch}
                            />
                        </Col>
                        <Col xs={12} sm={6} md={4}>
                            <Select 
                                style={{ width: '100%' }} 
                                value={filters.status}
                                onChange={val => setFilters({...filters, status: val})}
                            >
                                <Option value="all">All Status</Option>
                                <Option value="active">Active</Option>
                                <Option value="inactive">Inactive</Option>
                            </Select>
                        </Col>
                        <Col>
                            <Space>
                                <Button type="primary" onClick={handleSearch} icon={<SearchOutlined />}>Search</Button>
                                <Button onClick={handleClear} icon={<ClearOutlined />}>Clear</Button>
                            </Space>
                        </Col>
                    </Row>

                    <Table 
                        columns={columns}
                        dataSource={data}
                        rowKey="id"
                        loading={loading}
                        pagination={pagination}
                        onChange={handleTableChange}
                        bordered
                        size="small"
                    />
                </Card>

                <Modal
                    title={editingItem ? 'Edit Type' : 'Add New Type'}
                    open={modalVisible}
                    onCancel={() => setModalVisible(false)}
                    onOk={() => form.submit()}
                    confirmLoading={saveLoading}
                    destroyOnClose
                >
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSave}
                    >
                        <Form.Item
                            name="value"
                            label="Type Name"
                            rules={[{ required: true, message: 'Please enter type name' }]}
                        >
                            <Input placeholder="Enter type name" />
                        </Form.Item>
                        <Form.Item
                            name="status"
                            label="Status"
                            initialValue={1}
                        >
                            <Select>
                                <Option value={1}>Active</Option>
                                <Option value={0}>Inactive</Option>
                            </Select>
                        </Form.Item>
                    </Form>
                </Modal>
            </Content>
            <Footerdiv />
        </Layout>
    );
};

export default TypeManagement;
